// SCR-STAFF-923 — سیاست‌های دسترسی (AP-IAM-03؛ US-IAM-003، BR-IAM-006): الزام احراز دومرحله‌ای به ازای نقش. نقش‌های BR-IAM-006
// (مالک، مدیر سامانه، مدیر، حسابدار، پزشک) همیشه الزامی و غیرقابل تغییرند؛ مرکز می‌تواند 2FA را برای نقش‌های دیگر هم الزامی کند
// (PUT roles/{id} با requiresMfa — C-AGENT-378). AP-IAM-04: سیاست دید داده بالینی (US-IAM-033، BR-IAM-035؛ iam.policy.manage)؛
// محدودیت شبکه (US-IAM-052) فاز 3.
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import FormControlLabel from '@mui/material/FormControlLabel';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Snackbar from '@mui/material/Snackbar';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import { unwrap, type RoleView } from '@phoenix/api-client';
import { useAccess, useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { ErrorState, ListPage, LoadingState, toJalali } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { WriteNotice } from '../settings/common';
import { iamKeys, useRoleLabel, useRoles } from './common';

export const SCR_STAFF_923 = 'SCR-STAFF-923';
export const ACCESS_POLICIES_PATH = '/settings/access-policies';

const CLINICAL_POLICIES = ['BranchScoped', 'CareTeamOnly', 'TenantWide'] as const;

/** سیاست دید داده بالینی (US-IAM-033): سه گزینه با توضیح؛ تغییر با Audit قبل و بعد. */
function ClinicalVisibilitySection({ onSaved }: { onSaved: (message: string) => void }) {
  const { t } = useTranslation('iam');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const manage = useWriteAccess('iam.policy.manage');
  const settings = useQuery({
    queryKey: iamKeys.clinicalVisibility(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/identity/settings/clinical-visibility')),
  });
  const save = useMutation({
    mutationFn: (policy: string) => unwrap(tenantApi.PUT('/api/v1/identity/settings/clinical-visibility', { body: { policy } })),
    onSuccess: (view) => {
      queryClient.setQueryData(iamKeys.clinicalVisibility(session?.tenantId), view);
      onSaved(t('iam:policies.clinical_saved'));
    },
  });
  const labelId = 'clinical-visibility-title';

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="h2" sx={{ mb: 1 }} id={labelId}>
        {t('iam:policies.clinical_title')}
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 1.5 }}>
        {t('iam:policies.clinical_hint')}
      </Typography>
      {settings.isPending ? (
        <LoadingState rows={3} />
      ) : settings.error ? (
        <ErrorState error={settings.error} onRetry={() => void settings.refetch()} />
      ) : (
        <>
          <RadioGroup
            aria-labelledby={labelId}
            value={settings.data?.policy ?? 'BranchScoped'}
            onChange={(event) => save.mutate(event.target.value)}
            data-testid="clinical-visibility"
            data-policy={settings.data?.policy}
          >
            {CLINICAL_POLICIES.map((policy) => (
              <FormControlLabel
                key={policy}
                value={policy}
                disabled={!manage.canWrite || save.isPending}
                control={<Radio slotProps={{ input: { 'data-testid': `clinical-visibility-${policy}` } as object }} />}
                label={
                  <Stack sx={{ py: 0.5 }}>
                    <span>
                      {t(`iam:policies.clinical_options.${policy}.title`)}
                      {policy === 'BranchScoped' && (
                        <Chip size="small" sx={{ ms: 1 }} label={t('iam:policies.clinical_default')} />
                      )}
                    </span>
                    <Typography variant="body2" color="text.secondary">
                      {t(`iam:policies.clinical_options.${policy}.description`)}
                    </Typography>
                  </Stack>
                }
              />
            ))}
          </RadioGroup>
          {settings.data?.changedAt && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {t('iam:policies.clinical_changed', {
                name: settings.data.changedByName ?? t('common:states.none'),
                date: toJalali(settings.data.changedAt),
              })}
            </Typography>
          )}
        </>
      )}
      {save.error ? <ErrorState error={save.error} compact /> : null}
    </Paper>
  );
}

export function AccessPoliciesPage() {
  const { t } = useTranslation('iam');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const access = useAccess();
  const manage = useWriteAccess('iam.role.manage');
  const roleLabel = useRoleLabel();
  const roles = useRoles();
  const [notice, setNotice] = useState<string | null>(null);

  const toggle = useMutation({
    mutationFn: ({ role, requiresMfa }: { role: RoleView; requiresMfa: boolean }) =>
      unwrap(
        tenantApi.PUT('/api/v1/identity/roles/{id}', {
          params: { path: { id: role.roleId } },
          body: { name: null, requiresMfa },
        }),
      ),
    onSuccess: () => {
      setNotice(t('iam:policies.saved'));
      void queryClient.invalidateQueries({ queryKey: iamKeys.roles(session?.tenantId) });
    },
  });

  return (
    <ListPage screenId={SCR_STAFF_923} titleKey="iam:policies.title" subtitleKey="iam:policies.subtitle">
      <Stack spacing={2}>
        {access.can('iam.policy.manage') && <ClinicalVisibilitySection onSaved={setNotice} />}
        <WriteNotice write={manage} />
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="h2" sx={{ mb: 1 }}>
            {t('iam:policies.mfa_title')}
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 1.5 }}>
            {t('iam:policies.mfa_hint')}
          </Typography>
          {roles.isPending ? (
            <LoadingState rows={4} />
          ) : roles.error ? (
            <ErrorState error={roles.error} onRetry={() => void roles.refetch()} />
          ) : (
            <List dense data-testid="mfa-policies">
              {(roles.data?.items ?? []).map((role) => {
                const mandatory = Boolean(role.mfaMandatory);
                const label = roleLabel(role.code);
                return (
                  <ListItem
                    key={role.roleId}
                    divider
                    data-testid={`mfa-policy-${role.code}`}
                    data-required={Boolean(role.requiresMfa)}
                    secondaryAction={
                      <Switch
                        edge="end"
                        checked={Boolean(role.requiresMfa) || mandatory}
                        disabled={mandatory || role.isLocked || !manage.canWrite || toggle.isPending}
                        onChange={(event) => toggle.mutate({ role, requiresMfa: event.target.checked })}
                        slotProps={{ input: { 'aria-label': label } }}
                      />
                    }
                  >
                    <ListItemText
                      primary={
                        <Stack direction="row" spacing={1} alignItems="center">
                          <span>{label}</span>
                          {mandatory && <Chip size="small" label={t('iam:policies.mandatory')} />}
                        </Stack>
                      }
                    />
                  </ListItem>
                );
              })}
            </List>
          )}
          {toggle.error ? <ErrorState error={toggle.error} compact /> : null}
        </Paper>
        <Alert severity="info">{t('iam:policies.network_notice')}</Alert>
      </Stack>
      <Snackbar open={notice !== null} autoHideDuration={4000} onClose={() => setNotice(null)} message={notice} />
    </ListPage>
  );
}
